import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    Arrow,
    SeoContent,
    SeoContentTitle,
    SeoContentWrapper,
    ShowMoreOrLess,
    ShowMoreWrapper,
} from 'src/domains/layouts/webview/components/seoPages/SeoPages.style';
import { SeoPagesContentState } from 'src/domains/layouts/webview/components/seoPages/SeoPagesState';
import { useCommon } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

interface SeoPagesContentPropsType {
    pageSlug: string;
}

export const SeoPagesContent = observer(
    'SeoPagesContent',
    (props: SeoPagesContentPropsType): React.ReactElement | null => {
        const common = useCommon();

        if (common.features.isSeoPagesContentOn === false) {
            return null;
        }

        const state = SeoPagesContentState.get(common, props.pageSlug);
        const configState = ConfigComponents.get(common).config;
        const isCasino = ['casino', 'live-casino', 'virtuals'].includes(props.pageSlug);

        if (state.data === undefined) {
            const fallbackSeoMap = new Map<string, { title: string; description: string } | null>(
                Object.entries({
                    ...common.features.seoMeta,
                    ...common.features.seoMetaSport,
                })
            );

            if (common.features.seoMetaSpecialSports !== null && !fallbackSeoMap.has(props.pageSlug)) {
                const data = common.features.seoMetaSpecialSports.find((specialSport) =>
                    Object.hasOwn(specialSport, props.pageSlug)
                );

                if (data !== undefined) {
                    fallbackSeoMap.set(props.pageSlug, {
                        title: data[props.pageSlug]?.title ?? '',
                        description: data[props.pageSlug]?.description ?? '',
                    });
                }
            }

            const pageConfig = fallbackSeoMap.get(props.pageSlug);

            if (pageConfig === null || pageConfig === undefined) {
                return null;
            }

            const { title, description } = pageConfig;

            return (
                <SeoContentWrapper
                    isCasino={isCasino}
                    variant={configState.componentVariants.sportsbookVariant}
                >
                    {title !== '' && <SeoContentTitle isFallback={true}>{title}</SeoContentTitle>}
                    {description !== '' && <SeoContent>{description}</SeoContent>}
                </SeoContentWrapper>
            );
        }

        const languagesState = LanguagesState.get(common);
        const showLess = languagesState.getTranslation('seocontent.pages.show-less', 'Show Less');
        const showMore = languagesState.getTranslation('seocontent.pages.show-more', 'Show More');

        return (
            <SeoContentWrapper
                isCasino={isCasino}
                variant={configState.componentVariants.sportsbookVariant}
            >
                <SeoContentTitle>{state.data.title}</SeoContentTitle>
                <SeoContent>{state.data.short_description}</SeoContent>

                {state.data.full_description === '' ? null : (
                    <ShowMoreWrapper
                        isShowMore={state.isShowMore}
                        onClick={state.toggleShowMore}
                        isCasino={isCasino}
                    >
                        <Arrow position={state.isShowMore ? 'up' : 'right'} />
                        <ShowMoreOrLess data-test='show-more-seo-content'>
                            {state.isShowMore ? showLess : showMore}
                        </ShowMoreOrLess>
                    </ShowMoreWrapper>
                )}

                <SeoContent
                    isHidden={!state.isShowMore}
                    dangerouslySetInnerHTML={{ __html: state.data.full_description ?? '' }}
                />
            </SeoContentWrapper>
        );
    }
);
